// Procurement > Crab Cake Planner — replaces Carman's "Crab Cake Projections.xlsx".
//
// Carman, Oct 14, 2026: "Two Christmases ago a dozens-vs-units mix-up more than
// doubled our projection." So every quantity is STORED AS PACKS and shown as packs,
// cakes and dozens side by side; the count-mode toggle only changes what the
// boxes take as input. A pack is 12 cakes for 2 oz and 6 cakes for 4 oz and 6 oz,
// so a "dozen" is one pack of 2 oz but two packs of 4 oz. Never mix them up again.
//
// ⚠️ SALES COME BACK IN DOZENS. product_report_items rolls every crab cake SKU onto
// its "1 Dozen - N oz." master product, so total_qty is dozens of cakes, not packs.
// fillFromSales() converts dozens × 12 / cakes per pack.
//
// Fixes carried over from the old sheet (her list — do not reintroduce):
//   - Old Bay is 27 TBSP (81 tsp) a tub, not 27 tsp.
//   - Cost per pack divides by what is being MADE (a tub's packs), not by the
//     total wanted including stock already on hand.
//   - Material cost = what the run uses (per-tub share of a container), and
//     what to ORDER nets off containers already on hand.
//   - The $0.035 packaging bag is in the cost per pack.
//   - Crab pounds are calculated from tubs, never typed.
//
// Storage: lib/crab-cake-planner.js — `settings` (Save button) and `plan`
// (saved automatically as she types). Crab Cake Ratios runs live in plan.runs.
//
// CRAB CAKE RATIOS (Carman, Oct 14, 2026 — replaces the Crab Meat Log sheet): one
// sheet per production run, with the jumbo and backfin target pounds and the crab
// SKUs the crew pulls from. Targets come from the same settings as the planner
// (crab per tub / batches per tub = 12 lb a batch; regular = 25% jumbo, so 3 lb
// jumbo + 9 lb backfin a batch; All Jumbo = all jumbo). Printing opens the sheet
// in its own window, so the browser prints only the sheet and not CPx around it.
//
// CARMAN'S FIVE, Oct 27, 2026 (from presenting it to her team):
//   1. Ingredient prices come from QuickBooks: the newest crab cake bill line whose
//      description carries "@ $X/unit", converted to the container by "billed as".
//   2. Crab meat is costed by SKU (settings.crabCosts). Each run sheet is costed from the
//      meat it actually uses; the planner plans with the SKU picked for jumbo and backfin.
//   3. The Crab Place logo heads every printed sheet.
//   4. A walkthrough opens with the planner: prices, what we are making, what we need
//      against what we have.
//   5. Actual labor: TimeClock Plus "Crab Cakes" hours x ADP rates, server side, over the
//      packs the run sheets say were produced. Totals only ever reach this page.
//
// CARMAN'S THREE, Oct 29, 2026:
//   1. Cost per pack is the AVERAGE over confirmed runs of that size: every run's crab,
//      ingredients, packaging and labor added up, over every pack produced, so a big run
//      counts more than a small one. Each run's own cost per pack shows beside it.
//   2. Labor reads itself from the time clock for each run's date (no date pickers). A day's
//      Crab Cakes hours are split across that day's confirmed runs by packs produced.
//   3. "Confirm done" takes the packs produced and posts the run to CPX Catch as a
//      production batch (crab meat out, cakes in). Only confirmed runs count toward the
//      average and the labor; a run that is only a plan never does. The batch notes carry
//      the run id, so a second click finds the batch instead of posting it again.
//   Then (same day): confirmed runs move out of Crab Cake Ratios into History, under
//   Settings, and the run sheet prints the CPX Catch batch number for the tote labels.
//
// CARMAN'S TWO, Oct 30, 2026:
//   1. Finished units can go to more than one location: the home location (F02) takes
//      whatever isn't sent elsewhere, like overage into F01 when F02 is full.
//   2. In stock in By size reads itself from CPX Catch, every location added up, and is no
//      longer typed. A saved typed number only shows if Catch can't be read. Stock rises
//      when a run is confirmed.
//   Then (same day): Tubs done is gone. Confirming a run is what counts it, through In stock,
//      so a finished tub can't be counted twice. Any old saved "completed" is ignored.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { RefreshCw, Save, Settings as Gear, ChevronDown, ChevronRight, Printer, Plus, Trash2 } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';
import { LOCATIONS } from './Inventory';

// catchSku: the CPX Catch item a pack goes into (neon.js CRAB_CAKES, one pack = one unit).
// Where finished units can go: the real CPX Catch locations (batches refuse the two holds).
const PUT_AWAY_LOCS = Object.keys(LOCATIONS).filter((k) => !['P01', 'PF1'].includes(k));
const SIZES = [
  { key: '2oz', label: '2 oz', oz: 2, cakesPerPack: 12, jumboPct: 25, masterId: '40', catchSku: 'CP018' },
  { key: '4oz', label: '4 oz', oz: 4, cakesPerPack: 6, jumboPct: 25, masterId: '43', catchSku: 'CP016' },
  { key: '4ozaj', label: '4 oz All Jumbo', oz: 4, cakesPerPack: 6, jumboPct: 100, masterId: '1353', catchSku: 'CP017' },
  { key: '6oz', label: '6 oz', oz: 6, cakesPerPack: 6, jumboPct: 25, masterId: '46', catchSku: 'CP015' },
  { key: '6ozaj', label: '6 oz All Jumbo', oz: 6, cakesPerPack: 6, jumboPct: 100, masterId: '1000081', catchSku: 'CP458' },
];

// Per-tub amounts are in the CONTAINER's unit so the container math works.
// Container sizes and prices are from the old sheet (prices last updated Dec 2024);
// Dijon, panko and parsley per-tub weights are the old sheet's conversions too.
const DEFAULT_SETTINGS = {
  crabPerTub: 36,
  mixPerTub: 42.75,
  batchesPerTub: 3,
  jumboPrice: 26,
  backfinPrice: 18,
  packsPerCrewHour: 21,
  crewRate: 64.5,
  packagingPerPack: 0.035,
  // billedAs: the container in the units QuickBooks bills in, so a "$1.74/lb" line
  // becomes a 25 lb bag at $43.50.
  ingredients: [
    { key: 'panko', name: 'Panko', perBatch: '3 cups', tubMeasure: '9 cups', perTub: 15.75, unit: 'oz', container: '25 lb bag', containerSize: 400, price: 44.85, billedAs: '25 lb' },
    { key: 'oldbay', name: 'Old Bay', perBatch: '1/2 cup + 1 tbsp', tubMeasure: '27 tbsp', perTub: 81, unit: 'tsp', container: '7.5 lb', containerSize: 1416, price: 35.02, billedAs: '7.5 lb' },
    { key: 'parsley', name: 'Dried parsley', perBatch: '1/3 cup', tubMeasure: '1 cup', perTub: 27, unit: 'g', container: '10 oz', containerSize: 283.5, price: 22.85, billedAs: '10 oz' },
    { key: 'mayo', name: 'Mayonnaise', perBatch: '1 1/2 cups', tubMeasure: '4 1/2 cups', perTub: 4.5, unit: 'cups', container: '4 gallon bucket', containerSize: 64, price: 86.54, billedAs: '4 gal' },
    { key: 'eggs', name: 'Eggs', perBatch: '4', tubMeasure: '12', perTub: 12, unit: 'eggs', container: '30 dozen', containerSize: 360, price: 87.94, billedAs: '360 ea' },
    { key: 'worcestershire', name: 'Worcestershire', perBatch: '1 1/2 tbsp', tubMeasure: '4 1/2 tbsp', perTub: 4.5, unit: 'tbsp', container: '1 gallon', containerSize: 252, price: 7.95, billedAs: '1 gal' },
    { key: 'dijon', name: 'Dijon mustard', perBatch: '1/4 cup', tubMeasure: '3/4 cup', perTub: 7, unit: 'oz', container: '105 oz jar', containerSize: 105, price: 32.11, billedAs: '105 oz' },
  ],
  // $/lb by crab meat SKU, and which SKU the planner plans with for each grade. Blank
  // falls back to jumboPrice / backfinPrice above.
  crabCosts: {},
  jumboSku: '',
  backfinSku: '',
};

// Which QuickBooks bill lines are which ingredient.
const QB_MATCH = {
  panko: /panko|bread ?crumb/i,
  oldbay: /old ?bay/i,
  parsley: /parsley/i,
  mayo: /mayo|hellman/i,
  eggs: /\beggs?\b/i,
  worcestershire: /worcest|wrcst/i,
  dijon: /dijon/i,
};
// Everything weighed or poured goes through ounces (a gallon is 128 fluid ounces, which is
// how the bills are written); eggs count.
const TO_OZ = { lb: 16, oz: 1, gal: 128 };
const TO_EA = { ea: 1, dozen: 12 };
const parseBilled = (s) => {
  const m = String(s || '').trim().match(/^([\d.]+)\s*([a-z]+)/i);
  if (!m) return null;
  const unit = { lbs: 'lb', each: 'ea', doz: 'dozen', gallon: 'gal' }[m[2].toLowerCase()] || m[2].toLowerCase();
  return Number(m[1]) > 0 ? { qty: Number(m[1]), unit } : null;
};
// One QuickBooks line -> the price of one of our containers, or null if the units don't convert.
function containerPriceFrom(row, ing) {
  const c = parseBilled(ing.billedAs);
  if (!c) return null;
  let { price, unit } = row;
  // "(36) 16 oz. old bay @ $9.14/ea." is a 16 oz can, so $/ea becomes $/oz.
  if (unit === 'ea' && TO_OZ[c.unit]) {
    const size = String(row.description).match(/(\d+(?:\.\d+)?)\s*(oz|lb)\b/i);
    if (!size) return null;
    price /= Number(size[1]) * TO_OZ[size[2].toLowerCase()];
    unit = 'oz';
  }
  if (TO_OZ[unit] && TO_OZ[c.unit]) return (price / TO_OZ[unit]) * c.qty * TO_OZ[c.unit];
  if (TO_EA[unit] && TO_EA[c.unit]) return (price / TO_EA[unit]) * c.qty * TO_EA[c.unit];
  return null;
}
// The newest QuickBooks line that prices this ingredient.
function latestQbPrice(rows, ing) {
  const re = QB_MATCH[ing.key];
  if (!re) return null;
  for (const row of rows) {
    if (!re.test(row.description)) continue;
    const p = containerPriceFrom(row, ing);
    if (p != null && p > 0) return { ...row, containerPrice: Math.round(p * 100) / 100 };
  }
  return null;
}
// $/lb the planner plans with for a grade: the chosen SKU's cost, else the grade price.
const gradePrice = (st, grade) => {
  const sku = grade === 'jumbo' ? st.jumboSku : st.backfinSku;
  const v = sku ? n((st.crabCosts || {})[sku]) : 0;
  return v > 0 ? v : n(grade === 'jumbo' ? st.jumboPrice : st.backfinPrice);
};

// Crab meat SKUs on the ratio sheet. The Deluxe SKUs (DELUXE, Deluxe-B, Deluxe-J)
// are retired and deliberately left off. Names are Carman's (Oct 20, 2026): the
// crew pulling the meat knows it by name, not SKU, so the name leads everywhere.
const CRAB_SKUS = [
  { sku: 'CP005', grade: 'jumbo', name: 'Fresh Domestic Jumbo Lump' },
  { sku: 'CP005FZ', grade: 'jumbo', name: 'Fresh Domestic Jumbo Lump (Frozen)' },
  { sku: 'CP007', grade: 'jumbo', name: 'Pasteurized Domestic Jumbo Lump' },
  { sku: 'CP011', grade: 'jumbo', name: 'Pasteurized Imported Jumbo Lump' },
  { sku: 'CP011IF', grade: 'jumbo', name: 'Fresh Imported Jumbo Lump' },
  { sku: 'CP006', grade: 'backfin', name: 'Fresh Domestic Backfin' },
  { sku: 'CP488F', grade: 'backfin', name: 'Fresh Domestic Gourmet Lump' },
  { sku: 'CP008', grade: 'backfin', name: 'Pasteurized Domestic Backfin' },
  { sku: 'CP012', grade: 'backfin', name: 'Pasteurized Imported Backfin' },
  { sku: 'CP012IF', grade: 'backfin', name: 'Fresh Imported Backfin' },
];
const SECTION_IDS = ['sales', 'sizes', 'crab', 'run', 'ingredients', 'labor', 'ratios', 'settings', 'history'];

const blankPlan = () => ({
  mode: 'packs',
  sizes: Object.fromEntries(SIZES.map((s) => [s.key, { want: '', stock: '' }])),
  jumboOnHand: '',
  backfinOnHand: '',
  ingredientsOnHand: {},
  salesFrom: '',
  salesTo: '',
  salesDozens: null,
  runs: [],            // each run: { id, sizeKey, batches, date, lbs, packsMade, loose, confirmed }
});

const MODES = [['packs', 'Packs'], ['dozens', 'Dozens'], ['cakes', 'Cakes']];
const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const money = (v) => '$' + n(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (v, d = 1) => {
  const x = Math.round(n(v) * 10 ** d) / 10 ** d;
  return x.toLocaleString('en-US', { maximumFractionDigits: d });
};
const ceil = (v) => Math.max(0, Math.ceil(v - 1e-9));
const toPacks = (v, size, mode) => (mode === 'cakes' ? v / size.cakesPerPack : mode === 'dozens' ? (v * 12) / size.cakesPerPack : v);
const fromPacks = (p, size, mode) => (mode === 'cakes' ? p * size.cakesPerPack : mode === 'dozens' ? (p * size.cakesPerPack) / 12 : p);
const allUnits = (p, size) => `${fmt(p)} packs · ${fmt(p * size.cakesPerPack, 0)} cakes · ${fmt((p * size.cakesPerPack) / 12)} dz`;
const longDate = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const todayYmdLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// One run's targets and planned totals.
function runFigures(run, st) {
  const size = SIZES.find((x) => x.key === run.sizeKey) || SIZES[0];
  const batches = n(run.batches);
  const perBatch = n(st.batchesPerTub) > 0 ? n(st.crabPerTub) / n(st.batchesPerTub) : 12;
  const j = size.jumboPct / 100;
  const jumboTarget = batches * perBatch * j;
  const backfinTarget = batches * perBatch - jumboTarget;
  const lbs = run.lbs || {};
  const sum = (grade) => CRAB_SKUS.filter((c) => c.grade === grade).reduce((t, c) => t + n(lbs[c.sku]), 0);
  const jumboPlanned = sum('jumbo');
  const backfinPlanned = sum('backfin');
  const anyEntered = CRAB_SKUS.some((c) => lbs[c.sku] !== undefined && lbs[c.sku] !== '');
  const off = (a, b) => Math.abs(a - b) > 0.005;
  const tubs = n(st.batchesPerTub) > 0 ? batches / n(st.batchesPerTub) : 0;
  // What this run costs, from the meat it actually uses (Carman, Oct 27, 2026).
  const costs = st.crabCosts || {};
  const used = CRAB_SKUS.filter((c) => n(lbs[c.sku]) > 0);
  const crabCost = used.reduce((t, c) => t + n(lbs[c.sku]) * n(costs[c.sku]), 0);
  const noCost = used.filter((c) => !(n(costs[c.sku]) > 0));
  const packs = tubs * (n(st.mixPerTub) / ((size.oz * size.cakesPerPack) / 16));
  const ingCost = tubs * ingCostPerTub(st);
  return {
    size, batches, perBatch, tubs,
    jumboTarget, backfinTarget, jumboPlanned, backfinPlanned, anyEntered,
    jumboOff: anyEntered && off(jumboPlanned, jumboTarget), backfinOff: anyEntered && off(backfinPlanned, backfinTarget),
    jumboPerBatch: perBatch * j, backfinPerBatch: perBatch * (1 - j),
    crabCost, noCost, packs, ingCost,
    costPerPack: packs > 0 && used.length ? (crabCost + ingCost) / packs + n(st.packagingPerPack) : null,
  };
}

const ingCostPerTub = (st) => st.ingredients.reduce((s, i) => s + (n(i.containerSize) > 0 ? (n(i.price) * n(i.perTub)) / n(i.containerSize) : 0), 0);

// What each confirmed run actually cost, and the running average a pack for each size
// (Carman, Oct 29, 2026). A day's time clock labor is split across that day's confirmed runs
// by packs produced. laborDays is the time clock's answer: { 'YYYY-MM-DD': { hours, cost } }.
function runCosts(runs, st, laborDays) {
  const confirmed = (runs || []).filter((r) => r.confirmed && n(r.confirmed.packs) > 0);
  const packsOnDay = {};
  confirmed.forEach((r) => { packsOnDay[r.date] = (packsOnDay[r.date] || 0) + n(r.confirmed.packs); });
  const byRun = {};
  confirmed.forEach((r) => {
    const f = runFigures(r, st);
    const packs = n(r.confirmed.packs);
    const day = laborDays ? laborDays[r.date] : null;
    const share = packsOnDay[r.date] > 0 ? packs / packsOnDay[r.date] : 0;
    const labor = day ? n(day.cost) * share : 0;
    const hours = day ? n(day.hours) * share : 0;
    const packaging = packs * n(st.packagingPerPack);
    const material = f.crabCost + f.ingCost + packaging;
    byRun[r.id] = {
      packs, labor, hours, material, total: material + labor, noLabor: !day, noCost: f.noCost,
      perPack: (material + labor) / packs, perPackNoLabor: material / packs,
      sharedWith: confirmed.filter((x) => x.date === r.date).length - 1,
    };
  });
  const bySize = {};
  SIZES.forEach((s) => {
    const rs = confirmed.filter((r) => r.sizeKey === s.key).map((r) => byRun[r.id]);
    const packs = rs.reduce((t, x) => t + x.packs, 0);
    if (!packs) return;
    const material = rs.reduce((t, x) => t + x.material, 0);
    const labor = rs.reduce((t, x) => t + x.labor, 0);
    bySize[s.key] = { runs: rs.length, packs, perPack: (material + labor) / packs, perPackNoLabor: material / packs, laborPerPack: labor / packs };
  });
  const packs = confirmed.reduce((t, r) => t + byRun[r.id].packs, 0);
  const labor = confirmed.reduce((t, r) => t + byRun[r.id].labor, 0);
  const priced = confirmed.filter((r) => !byRun[r.id].noLabor);
  const pricedPacks = priced.reduce((t, r) => t + byRun[r.id].packs, 0);
  return { byRun, bySize, packs, labor, laborPerPack: pricedPacks > 0 ? labor / pricedPacks : null };
}

// The planner's labor cost a pack: actual (time clock over confirmed runs' packs) once there
// is any, otherwise the crew-rate estimate from settings.
function laborFigures(costs, st) {
  const est = n(st.packsPerCrewHour) > 0 ? n(st.crewRate) / n(st.packsPerCrewHour) : 0;
  const actual = costs && costs.laborPerPack > 0 ? costs.laborPerPack : null;
  return { perPack: actual != null ? actual : est, estimate: est, actual };
}

const PRINT_CSS = `
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; }
  .page { padding: 28px 32px; page-break-after: always; }
  .page:last-child { page-break-after: auto; }
  h1 { font-size: 20px; margin: 0 0 4px; color: #00395C; }
  .brand { display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 2px solid #00395C; padding-bottom: 8px; margin-bottom: 12px; }
  .brand img { height: 64px; }
  .brand h1 { margin: 0; }
  h2 { font-size: 14px; margin: 18px 0 6px; color: #00395C; text-transform: uppercase; letter-spacing: .5px; }
  .meta { font-size: 13px; margin-bottom: 10px; }
  .meta span { margin-right: 22px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { border: 1px solid #999; padding: 7px 8px; text-align: left; }
  th { background: #eef2f6; font-size: 11px; text-transform: uppercase; letter-spacing: .4px; }
  td.r, th.r { text-align: right; }
  tr.tot td { font-weight: bold; background: #f7f7f7; }
  .flag { color: #b00020; font-weight: bold; }
  table.write td { height: 34px; }   /* room to write by hand (Carman, Oct 23, 2026) */
  /* Copied onto the tote labels by hand, so it reads at arm's length (Carman, Oct 29, 2026). */
  .batch { font-size: 18px; font-weight: bold; letter-spacing: .5px; }
  /* Reference only, so it sits last and small: the run is what the sheet is for (Carman, Oct 26, 2026). */
  table.recipe { width: 60%; font-size: 11px; }
  table.recipe td { padding: 3px 6px; }
  h2.recipe { font-size: 11px; margin-top: 24px; }
  .sign { display: flex; gap: 40px; margin-top: 28px; font-size: 13px; }
  .sign div { flex: 1; border-top: 1px solid #111; padding-top: 4px; }
  @page { margin: 0.4in; }
`;

// The print window is about:blank, so the logo needs the full address (Carman, Oct 27, 2026).
const brandHead = (title) => `<div class="brand"><h1>${title}</h1><img src="${window.location.origin}/crab-place-logo.png" alt="The Crab Place"></div>`;

function runSheetHtml(run, st) {
  const f = runFigures(run, st);
  const lbs = run.lbs || {};
  const listed = f.anyEntered ? CRAB_SKUS.filter((c) => n(lbs[c.sku]) > 0) : CRAB_SKUS;
  const row = (c) => `<tr><td>${esc(c.name)}</td><td>${esc(c.sku)}</td>`
    + `<td class="r">${f.anyEntered ? fmt(lbs[c.sku], 2) : ''}</td><td></td><td></td><td></td></tr>`;
  const tot = (label, planned, target, isOff) => `<tr class="tot"><td colspan="2">${label} total (target ${fmt(target, 2)} lb)</td>`
    + `<td class="r">${f.anyEntered ? fmt(planned, 2) : ''}${isOff ? ' <span class="flag">does not match</span>' : ''}</td><td></td><td colspan="2"></td></tr>`;
  const recipe = [
    `<tr><td>Crab</td><td>${f.size.jumboPct === 100 ? `${fmt(f.perBatch, 2)} lb jumbo` : `${fmt(f.jumboPerBatch, 2)} lb jumbo + ${fmt(f.backfinPerBatch, 2)} lb backfin`}</td></tr>`,
    ...st.ingredients.map((i) => `<tr><td>${esc(i.name)}</td><td>${esc(i.perBatch)}</td></tr>`),
  ].join('');
  return `<div class="page">
    ${brandHead(`Crab Cake Ratios: ${esc(f.size.label)}`)}
    <div class="meta"><span><b>Date:</b> ${esc(longDate(run.date)) || '______________'}</span>
      <span><b>Batch:</b> ${run.confirmed && run.confirmed.batch ? `<span class="batch">${esc(run.confirmed.batch)}</span>` : '______________'}</span>
      <span><b>Batches:</b> ${fmt(f.batches, 2)} (${fmt(f.tubs, 2)} tub${f.tubs === 1 ? '' : 's'})</span>
      <span><b>Jumbo target:</b> ${fmt(f.jumboTarget, 2)} lb</span><span><b>Backfin target:</b> ${fmt(f.backfinTarget, 2)} lb</span></div>
    <h2>Crab</h2>
    <table><thead><tr><th>Crab meat</th><th>SKU</th><th class="r">Planned lb</th><th class="r">Used lb</th><th>Time</th><th>Initials</th></tr></thead>
      <tbody>${listed.filter((c) => c.grade === 'jumbo').map(row).join('')}${tot('Jumbo', f.jumboPlanned, f.jumboTarget, f.jumboOff)}
      ${listed.filter((c) => c.grade === 'backfin').map(row).join('')}${f.size.jumboPct === 100 && f.anyEntered && f.backfinPlanned === 0 ? '' : tot('Backfin', f.backfinPlanned, f.backfinTarget, f.backfinOff)}</tbody></table>
    <h2>Production</h2>
    <table class="write"><tbody>
      <tr><td style="width:42%"><b>Units produced this run</b> (${esc(f.size.label)})</td><td>${run.confirmed ? `${fmt(run.confirmed.packs, 0)} packs${run.confirmed.loose ? `, ${fmt(run.confirmed.loose, 0)} loose` : ''}` : ''}</td></tr>
      <tr><td><b>CPX Catch batch number</b> (for the tote labels)</td><td class="batch">${run.confirmed && run.confirmed.batch ? esc(run.confirmed.batch) : ''}</td></tr>
      <tr><td><b>Entered into inventory / WOS</b> (signed)</td><td></td></tr>
      <tr><td><b>Date entered</b></td><td></td></tr>
    </tbody></table>
    <div class="sign"><div>Made by</div><div>Checked by</div></div>
    <h2 class="recipe">Recipe per batch</h2>
    <table class="recipe"><tbody>${recipe}</tbody></table>
  </div>`;
}

function summaryHtml(calc, plan) {
  const sizeRows = calc.rows.map((r) => `<tr><td>${esc(r.size.label)}</td><td class="r">${fmt(r.want)}</td><td class="r">${fmt(r.stock)}</td>`
    + `<td class="r">${fmt(r.toMake)}</td><td class="r">${r.tubs}</td>`
    + `<td class="r">${fmt(r.jumboLb)}</td><td class="r">${fmt(r.backfinLb)}</td></tr>`).join('')
    + `<tr class="tot"><td colspan="5">Total needed</td><td class="r">${fmt(calc.jumboNeeded)}</td><td class="r">${fmt(calc.backfinNeeded)}</td></tr>`
    + `<tr><td colspan="5">Less crab on hand</td><td class="r">${fmt(plan.jumboOnHand)}</td><td class="r">${fmt(plan.backfinOnHand)}</td></tr>`
    + `<tr class="tot"><td colspan="5">To order</td><td class="r">${fmt(calc.jumboOrder)}</td><td class="r">${fmt(calc.backfinOrder)}</td></tr>`;
  const ingRows = calc.ingredients.map((i) => `<tr><td>${esc(i.name)}</td><td>${esc(i.container)}</td><td class="r">${i.containers}</td><td class="r">${fmt(i.onHand)}</td><td class="r">${i.order}</td><td class="r">${money(i.orderCost)}</td></tr>`).join('');
  return `<div class="page">
    ${brandHead('Crab Cake Planner')}
    <div class="meta"><span><b>Printed:</b> ${esc(longDate(todayYmdLocal()))}</span>${plan.salesFrom && plan.salesTo ? `<span><b>Sales range:</b> ${esc(longDate(plan.salesFrom))} to ${esc(longDate(plan.salesTo))}</span>` : ''}</div>
    <h2>By size (packs)</h2>
    <table><thead><tr><th>Size</th><th class="r">Want</th><th class="r">In stock</th><th class="r">To make</th><th class="r">Tubs</th><th class="r">Jumbo lb</th><th class="r">Backfin lb</th></tr></thead><tbody>${sizeRows}</tbody></table>
    <h2>Crab</h2>
    <table><thead><tr><th>Grade</th><th class="r">Needed lb</th><th class="r">On hand lb</th><th class="r">To order lb</th></tr></thead><tbody>
      <tr><td>Jumbo lump</td><td class="r">${fmt(calc.jumboNeeded)}</td><td class="r">${fmt(plan.jumboOnHand)}</td><td class="r">${fmt(calc.jumboOrder)}</td></tr>
      <tr><td>Backfin</td><td class="r">${fmt(calc.backfinNeeded)}</td><td class="r">${fmt(plan.backfinOnHand)}</td><td class="r">${fmt(calc.backfinOrder)}</td></tr></tbody></table>
    <h2>Ingredients (containers)</h2>
    <table><thead><tr><th>Ingredient</th><th>Container</th><th class="r">Needed</th><th class="r">On hand</th><th class="r">To order</th><th class="r">Cost</th></tr></thead><tbody>${ingRows}</tbody></table>
    <h2>The run</h2>
    <table><tbody>
      <tr><td>Tubs to make</td><td class="r">${fmt(calc.tubsLeft, 0)}</td></tr><tr><td>Crew hours</td><td class="r">${fmt(calc.crewHours)}</td></tr>
      <tr><td>Total run cost</td><td class="r">${money(calc.totalRun)}</td></tr><tr><td>Cash to order</td><td class="r">${money(calc.cashToOrder)}</td></tr></tbody></table>
  </div>`;
}

function printPages(pages, title) {
  const w = window.open('', '_blank');
  if (!w) { window.alert('Your browser blocked the print window. Allow pop-ups for CPx and try again.'); return; }
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${PRINT_CSS}</style></head><body>${pages.join('')}</body></html>`);
  w.document.close();
  w.focus();
  // Wait for the logo, or the first page prints without it.
  const imgs = Array.from(w.document.images);
  const ready = Promise.all(imgs.map((i) => (i.complete ? null : new Promise((r) => { i.onload = r; i.onerror = r; }))));
  Promise.race([ready, new Promise((r) => setTimeout(r, 2500))]).then(() => w.print());
}

// A collapsible card. Open/closed is remembered per browser (see openMap).
function Section({ id, title, extra, open, onToggle, style, children }) {
  return (
    <div className="card" style={{ marginBottom: 16, ...style }}>
      <div
        className="card-header" role="button" tabIndex={0} aria-expanded={open}
        onClick={() => onToggle(id)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(id); } }}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, userSelect: 'none' }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>{title}</span>
        {extra}
      </div>
      {open ? children : null}
    </div>
  );
}

const canEditSettings = () => {
  const u = getCurrentUser() || {};
  const roles = Array.isArray(u.roles) ? u.roles : String(u.roles || u.role || '').split(',').map((r) => r.trim());
  return roles.some((r) => ['Admin', 'Manager', 'Procurement'].includes(r));
};

function computePlan(plan, st, costs, liveStock) {
  const ingPerTub = ingCostPerTub(st);
  const labor0 = laborFigures(costs, st);
  const laborPerPack = labor0.perPack;
  const jumboPrice = gradePrice(st, 'jumbo');
  const backfinPrice = gradePrice(st, 'backfin');
  const rows = SIZES.map((size) => {
    const inp = plan.sizes[size.key] || {};
    const lbPerPack = (size.oz * size.cakesPerPack) / 16;
    const packsPerTub = n(st.mixPerTub) / lbPerPack;
    const want = n(inp.want);
    const live = liveStock && liveStock[size.key];
    const stock = live ? live.total : n(inp.stock);
    const toMake = Math.max(0, want - stock);
    const tubs = packsPerTub > 0 ? ceil(toMake / packsPerTub) : 0;
    const willMake = tubs * packsPerTub;
    const j = size.jumboPct / 100;
    const jumboLb = tubs * n(st.crabPerTub) * j;
    const backfinLb = tubs * n(st.crabPerTub) - jumboLb;
    const crabCostPerTub = n(st.crabPerTub) * (j * jumboPrice + (1 - j) * backfinPrice);
    const estPerPack = packsPerTub > 0 ? (crabCostPerTub + ingPerTub) / packsPerTub + n(st.packagingPerPack) : 0;
    // Once a size has confirmed runs, its cost per pack is their average (Carman, Oct 29, 2026).
    const avg = costs && costs.bySize[size.key];
    const costPerPack = avg ? avg.perPackNoLabor : estPerPack;
    return {
      size, lbPerPack, packsPerTub, want, stock, toMake, tubs, willMake, extra: willMake - toMake,
      packsLeft: willMake, jumboLb, backfinLb, costPerPack, estPerPack, avg,
      costWithLabor: avg ? avg.perPack : costPerPack + laborPerPack,
    };
  });
  const tubsLeft = rows.reduce((s, r) => s + r.tubs, 0);
  const packsLeft = rows.reduce((s, r) => s + r.packsLeft, 0);
  const jumboNeeded = rows.reduce((s, r) => s + r.jumboLb, 0);
  const backfinNeeded = rows.reduce((s, r) => s + r.backfinLb, 0);
  const jumboOrder = Math.max(0, jumboNeeded - n(plan.jumboOnHand));
  const backfinOrder = Math.max(0, backfinNeeded - n(plan.backfinOnHand));
  const ingredients = st.ingredients.map((i) => {
    const amount = tubsLeft * n(i.perTub);
    const containers = n(i.containerSize) > 0 ? ceil(amount / n(i.containerSize)) : 0;
    const onHand = n(plan.ingredientsOnHand[i.key]);
    const order = Math.max(0, containers - onHand);
    const usedCost = n(i.containerSize) > 0 ? (amount / n(i.containerSize)) * n(i.price) : 0;
    return { ...i, amount, containers, onHand, order, orderCost: order * n(i.price), usedCost, missing: !(n(i.containerSize) > 0 && n(i.price) > 0) };
  });
  const crabUsed = jumboNeeded * jumboPrice + backfinNeeded * backfinPrice;
  const ingUsed = ingredients.reduce((s, i) => s + i.usedCost, 0);
  const packaging = packsLeft * n(st.packagingPerPack);
  const crewHours = n(st.packsPerCrewHour) > 0 ? packsLeft / n(st.packsPerCrewHour) : 0;
  const labor = packsLeft * laborPerPack;
  const crabOrderCost = jumboOrder * jumboPrice + backfinOrder * backfinPrice;
  const ingOrderCost = ingredients.reduce((s, i) => s + i.orderCost, 0);
  return {
    rows, ingCostPerTub: ingPerTub, laborPerPack, laborActual: labor0.actual != null, jumboPrice, backfinPrice, tubsLeft, packsLeft, jumboNeeded, backfinNeeded, jumboOrder, backfinOrder,
    ingredients, crabUsed, ingUsed, packaging, crewHours, labor, totalRun: crabUsed + ingUsed + packaging + labor,
    crabOrderCost, ingOrderCost, cashToOrder: crabOrderCost + ingOrderCost,
  };
}

export default function CrabCakePlanner() {
  const [plan, setPlan] = useState(blankPlan());
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saveState, setSaveState] = useState('');
  const [salesBusy, setSalesBusy] = useState(false);
  // What she is typing, verbatim, while a count box has focus — so "1." is not
  // snapped back to "1" by the packs round trip mid-keystroke.
  const [typing, setTyping] = useState({});
  const [printSummary, setPrintSummary] = useState(true);
  const [qbRows, setQbRows] = useState(null);       // QuickBooks price lines, once pulled
  const [qbBusy, setQbBusy] = useState(false);
  const [qbPick, setQbPick] = useState({});         // ingredient key -> update it?
  const [laborBusy, setLaborBusy] = useState(false);
  const [laborDays, setLaborDays] = useState(null);  // time clock answer for the run dates
  const [confirming, setConfirming] = useState(null); // run id being posted to CPX Catch
  // Shown beside the Confirm done button: the page's error card is a long scroll above the run,
  // so a blank packs box looked like the button did nothing (Carman, Oct 30, 2026).
  const [runErr, setRunErr] = useState(null); // { id, msg }
  const [catchStock, setCatchStock] = useState(null); // { bySize: { key: { total, home, locs } }, at }
  const [stockErr, setStockErr] = useState('');
  // The walkthrough opens with the page unless she has turned it off in this browser.
  const [intro, setIntro] = useState(() => {
    try { return localStorage.getItem('ccp.introOff') === '1' ? null : 0; } catch { return 0; }
  });
  // Which sections are open, per browser. Settings starts closed, the rest open.
  const [openMap, setOpenMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ccp.open') || '{}') || {}; } catch { return {}; }
  });
  const isOpen = (id) => (openMap[id] !== undefined ? openMap[id] : id !== 'settings');
  const setOpen = (next) => {
    setOpenMap(next);
    try { localStorage.setItem('ccp.open', JSON.stringify(next)); } catch { /* private window */ }
  };
  const toggle = (id) => setOpen({ ...openMap, [id]: !isOpen(id) });
  const setAll = (v) => setOpen(Object.fromEntries(SECTION_IDS.map((id) => [id, v])));
  const loaded = useRef(false);
  const timer = useRef(null);
  const editable = canEditSettings();
  const by = getCurrentUser()?.staffName || '';

  useEffect(() => {
    (async () => {
      try {
        const out = (await neonQuery('crab_cake_planner_get')) || {};
        if (out.settings?.data) {
          const saved = out.settings.data;
          // Merge so a setting added later still gets its default.
          setSettings({
            ...DEFAULT_SETTINGS, ...saved,
            ingredients: DEFAULT_SETTINGS.ingredients.map((d) => ({ ...d, ...((saved.ingredients || []).find((x) => x.key === d.key) || {}) })),
          });
        }
        if (out.plan?.data) {
          const base = blankPlan();
          setPlan({ ...base, ...out.plan.data, sizes: { ...base.sizes, ...(out.plan.data.sizes || {}) }, runs: out.plan.data.runs || [] });
        }
        setMeta({ plan: out.plan, settings: out.settings });
      } catch (e) {
        setErr(e.message);
      } finally {
        loaded.current = true;
        setLoading(false);
      }
    })();
  }, []);

  // The plan saves itself a moment after the last keystroke, like the spreadsheet did.
  const savePlan = useCallback((next) => {
    clearTimeout(timer.current);
    setSaveState('Saving…');
    timer.current = setTimeout(async () => {
      try {
        await neonQuery('crab_cake_planner_save', { key: 'plan', data: next, by });
        setSaveState(`Saved ${new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`);
      } catch (e) {
        setSaveState('');
        setErr(`Could not save: ${e.message}`);
      }
    }, 800);
  }, [by]);

  const update = (fn) => {
    setPlan((p) => {
      const next = fn(p);
      if (loaded.current) savePlan(next);
      return next;
    });
  };

  const costs = useMemo(() => runCosts(plan.runs, settings, laborDays && laborDays.days), [plan.runs, settings, laborDays]);
  const calc = useMemo(() => computePlan(plan, settings, costs, catchStock && catchStock.bySize), [plan, settings, costs, catchStock]);
  const mode = plan.mode;

  const setSize = (key, field, raw) => {
    const size = SIZES.find((s) => s.key === key);
    update((p) => {
      let v = raw;
      if (raw !== '') v = String(Math.round(toPacks(n(raw), size, p.mode) * 1000) / 1000);
      return { ...p, sizes: { ...p.sizes, [key]: { ...p.sizes[key], [field]: v } } };
    });
  };
  const shown = (key, field) => {
    if (typing[`${key}.${field}`] !== undefined) return typing[`${key}.${field}`];
    const raw = plan.sizes[key]?.[field];
    if (raw === '' || raw == null) return '';
    const size = SIZES.find((s) => s.key === key);
    return String(Math.round(fromPacks(n(raw), size, mode) * 100) / 100);
  };

  const fillFromSales = async () => {
    if (!plan.salesFrom || !plan.salesTo) { setErr('Pick a start and end date for the sales range.'); return; }
    setErr('');
    setSalesBusy(true);
    try {
      const items = (await neonQuery('product_report_items', { startDate: plan.salesFrom, endDate: plan.salesTo })) || [];
      const dozens = {};
      SIZES.forEach((s) => {
        const row = items.find((r) => String(r.master_id) === s.masterId);
        dozens[s.key] = row ? n(row.total_qty) : 0;
      });
      update((p) => ({
        ...p,
        salesDozens: dozens,
        sizes: Object.fromEntries(SIZES.map((s) => [s.key, {
          ...p.sizes[s.key],
          want: String(Math.round(((dozens[s.key] * 12) / s.cakesPerPack) * 1000) / 1000),
        }])),
      }));
    } catch (e) {
      setErr(e.message);
    } finally {
      setSalesBusy(false);
    }
  };

  // Newest QuickBooks price for each ingredient, and which ones differ from settings.
  const qbFound = useMemo(() => (qbRows ? settings.ingredients.map((i) => ({ ing: i, qb: latestQbPrice(qbRows, i) })) : []), [qbRows, settings]);
  const qbChanged = qbFound.filter((x) => x.qb && Math.abs(x.qb.containerPrice - n(x.ing.price)) >= 0.01);
  const pullQb = async () => {
    setQbBusy(true);
    setErr('');
    try {
      const rows = (await neonQuery('crab_cake_planner_qb_prices')) || [];
      setQbRows(rows);
      const pick = {};
      settings.ingredients.forEach((i) => { pick[i.key] = !!latestQbPrice(rows, i); });
      setQbPick(pick);
    } catch (e) {
      setErr(`Could not read QuickBooks: ${e.message}`);
    } finally {
      setQbBusy(false);
    }
  };
  const applyQbPrices = async () => {
    const next = {
      ...settings,
      ingredients: settings.ingredients.map((i) => {
        const hit = qbFound.find((x) => x.ing.key === i.key);
        return hit && hit.qb && qbPick[i.key] ? { ...i, price: hit.qb.containerPrice, priceFrom: `${hit.qb.vendor || 'QuickBooks'}, ${hit.qb.date}` } : i;
      }),
    };
    try {
      await neonQuery('crab_cake_planner_save', { key: 'settings', data: next, by });
      setSettings(next);
      setSettingsDraft(null);
      setMeta((m) => ({ ...m, settings: { updatedBy: by, updatedAt: new Date().toISOString() } }));
      return true;
    } catch (e) {
      setErr(`Could not save the prices: ${e.message}`);
      return false;
    }
  };

  // Labor reads itself from the time clock for every run date: on open, when a run's date
  // changes, after a run is confirmed, and every 10 minutes while the page is open.
  const runDates = [...new Set((plan.runs || []).map((r) => r.date).filter(Boolean))].sort().join(',');
  const loadLabor = useCallback(async () => {
    if (!runDates) { setLaborDays({ days: {} }); return; }
    setLaborBusy(true);
    try {
      setLaborDays(await neonQuery('crab_cake_planner_labor_days', { dates: runDates.split(',') }));
    } catch (e) {
      setErr(`Could not read the time clock: ${e.message}`);
    } finally {
      setLaborBusy(false);
    }
  }, [runDates]);
  useEffect(() => {
    if (loading) return undefined;
    loadLabor();
    const t = setInterval(loadLabor, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [loading, loadLabor]);
  const labor = laborFigures(costs, settings);

  // In stock reads itself from CPX Catch: on open, after a run is confirmed, when she comes back
  // to the tab, and every 5 minutes. Daily Deduct can leave a location negative, so it's netted.
  const loadStock = useCallback(async () => {
    try {
      const look = await neonQuery('crab_cake_planner_catch_lookup', { skus: SIZES.map((x) => x.catchSku), runIds: [] });
      const bySize = {};
      SIZES.forEach((x) => {
        const it = (look.items || []).find((i) => i.sku === x.catchSku);
        if (!it) return;
        const locs = it.stock || [];
        bySize[x.key] = { total: locs.reduce((sum, l) => sum + n(l.qty), 0), home: it.home_loc || 'F02', locs };
      });
      setCatchStock({ bySize, at: new Date() });
      setStockErr('');
    } catch (e) {
      setStockErr(e.message);
    }
  }, []);
  useEffect(() => {
    if (loading) return undefined;
    loadStock();
    const t = setInterval(loadStock, 5 * 60 * 1000);
    window.addEventListener('focus', loadStock);
    return () => { clearInterval(t); window.removeEventListener('focus', loadStock); };
  }, [loading, loadStock]);

  // "Confirm done" (Carman, Oct 29, 2026): post the run to CPX Catch as a production batch,
  // the crab meat on the run sheet out and the packs produced in, then lock the run.
  const confirmRun = async (run) => {
    const f = runFigures(run, settings);
    const packs = Number(run.packsMade);
    const loose = run.loose === '' || run.loose == null ? 0 : Number(run.loose);
    const looseMax = f.size.cakesPerPack - 1;
    const lbs = CRAB_SKUS.filter((c) => n((run.lbs || {})[c.sku]) > 0).map((c) => ({ ...c, qty: n(run.lbs[c.sku]) }));
    const problems = [];
    if (!run.date) problems.push('Give the run a date.');
    if (!(Number.isInteger(packs) && packs > 0)) problems.push('Enter the packs produced, a whole number.');
    if (!(Number.isInteger(loose) && loose >= 0 && loose <= looseMax)) problems.push(`Loose cakes left over must be 0 to ${looseMax}.`);
    if (!lbs.length) problems.push('Enter the pounds of crab meat this run used.');
    if (lbs.some((c) => !Number.isInteger(c.qty))) problems.push('CPX Catch counts crab meat in whole pounds. Round the pounds on this run.');
    const elsewhere = (run.putAway || []).filter((x) => x.qty !== '' && x.qty != null).map((x) => ({ loc: x.loc, qty: Number(x.qty) }));
    const elsewhereTotal = elsewhere.reduce((sum, x) => sum + x.qty, 0);
    if (elsewhere.some((x) => !(Number.isInteger(x.qty) && x.qty > 0))) problems.push('Packs going to another location must be whole numbers.');
    else if (Number.isInteger(packs) && elsewhereTotal > packs) problems.push(`That sends ${elsewhereTotal} packs to other locations, more than the ${packs} produced.`);
    if (problems.length) { setRunErr({ id: run.id, msg: problems.join(' ') }); return; }
    setRunErr(null);
    setConfirming(run.id);
    try {
      const look = await neonQuery('crab_cake_planner_catch_lookup', { skus: [f.size.catchSku, ...lbs.map((c) => c.sku)], runIds: [run.id] });
      let batch = (look.posted || []).find((b) => String(b.notes || '').includes(run.id));
      let result = {};
      const item = (sku) => (look.items || []).find((i) => i.sku === sku);
      // The home location takes whatever isn't sent elsewhere (Carman, Oct 30, 2026).
      const outLoc = item(f.size.catchSku)?.home_loc || 'F02';
      const byLoc = { [outLoc]: packs - elsewhereTotal };
      elsewhere.forEach((x) => { byLoc[x.loc] = (byLoc[x.loc] || 0) + x.qty; });
      const outputs = Object.entries(byLoc).filter(([, qty]) => qty > 0).map(([loc, qty]) => ({ loc, qty }));
      if (!batch) {
        const missing = [f.size.catchSku, ...lbs.map((c) => c.sku)].filter((s) => !item(s));
        if (missing.length) throw new Error(`${missing.join(', ')} is not an item in CPX Catch.`);
        // Take each meat from its home location when that covers it, else the location that does.
        const fromLoc = (it, qty) => {
          const real = (it.stock || []).filter((s) => !['P01', 'PF1'].includes(s.loc));
          const home = real.find((s) => s.loc === it.home_loc);
          if (home && home.qty >= qty) return home.loc;
          return (real.find((s) => s.qty >= qty) || {}).loc || it.home_loc;
        };
        const cake = item(f.size.catchSku);
        const inputs = lbs.map((c) => { const it = item(c.sku); return { item_id: it.id, loc: fromLoc(it, c.qty), qty: c.qty }; });
        const msg = `Post this run to CPX Catch?\n\n${packs} packs of ${f.size.label}: ${outputs.map((o) => `${o.qty} into ${o.loc}`).join(', ')}${loose ? `, ${loose} loose cakes left over` : ''}\n`
          + lbs.map((c, i) => `${fmt(c.qty, 0)} lb ${c.name} out of ${inputs[i].loc}`).join('\n')
          + '\n\nOnce posted, this run is locked, moves to History and counts toward the average cost per pack.';
        if (!window.confirm(msg)) return;
        result = await neonQuery('ims_batch_post', {
          batch_type: 'standard', inputs, outputs: outputs.map((o) => ({ item_id: cake.id, ...o })), loose_cakes: loose,
          notes: `Crab Cake Planner run ${run.id}: ${f.size.label}, ${longDate(run.date)}`,
        });
        batch = { batch_number: result.batch_number };
      }
      const confirmed = { at: new Date().toISOString(), by, packs, loose, outputs, batch: batch.batch_number, pct: result.pct ?? null, flagged: !!result.flagged };
      // Saved straight away, not on the typing timer: the run is in CPX Catch now.
      const next = { ...plan, runs: plan.runs.map((r) => (r.id === run.id ? { ...r, packsMade: String(packs), loose: String(loose), confirmed } : r)) };
      clearTimeout(timer.current);
      setPlan(next);
      await neonQuery('crab_cake_planner_save', { key: 'plan', data: next, by });
      setSaveState(`Saved ${new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`);
      loadLabor();
      loadStock();
    } catch (e) {
      setRunErr({ id: run.id, msg: `Could not confirm the run: ${e.message}` });
    } finally {
      setConfirming(null);
    }
  };

  // The walkthrough's first question needs the QuickBooks prices, so fetch them as it opens.
  useEffect(() => {
    if (intro === 0 && !loading && !qbRows && !qbBusy) pullQb();
  }, [intro, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeIntro = () => setIntro(null);
  const setIntroOff = (off) => { try { localStorage.setItem('ccp.introOff', off ? '1' : '0'); } catch { /* private window */ } };

  const saveSettings = async () => {
    try {
      await neonQuery('crab_cake_planner_save', { key: 'settings', data: settingsDraft, by });
      setSettings(settingsDraft);
      setSettingsDraft(null);
      setMeta((m) => ({ ...m, settings: { updatedBy: by, updatedAt: new Date().toISOString() } }));
    } catch (e) {
      setErr(`Could not save settings: ${e.message}`);
    }
  };

  const newRun = (sizeKey, batches) => ({
    id: `run-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    sizeKey, batches: String(batches), date: todayYmdLocal(), lbs: {},
  });
  // One run per size that still has tubs left, at that many tubs' worth of batches.
  const addRunsFromPlan = () => {
    const bpt = n(settings.batchesPerTub) || 3;
    const add = calc.rows.filter((r) => r.tubs > 0).map((r) => newRun(r.size.key, r.tubs * bpt));
    if (!add.length) { setErr('No size has tubs left to make. Fill in "want on hand" first.'); return; }
    setErr('');
    update((p) => ({ ...p, runs: [...(p.runs || []), ...add] }));
  };
  const addRun = () => update((p) => ({ ...p, runs: [...(p.runs || []), newRun(SIZES[0].key, n(settings.batchesPerTub) || 3)] }));
  // A confirmed run is in CPX Catch, so it no longer changes here.
  const setRun = (id, field, v) => update((p) => ({ ...p, runs: p.runs.map((r) => (r.id === id && !r.confirmed ? { ...r, [field]: v } : r)) }));
  const setRunLb = (id, sku, v) => update((p) => ({ ...p, runs: p.runs.map((r) => (r.id === id && !r.confirmed ? { ...r, lbs: { ...(r.lbs || {}), [sku]: v } } : r)) }));
  const removeRun = (id) => {
    const r = (plan.runs || []).find((x) => x.id === id);
    const ask = r && r.confirmed
      ? `Remove this run? It drops out of the average cost per pack. Batch ${r.confirmed.batch} stays in CPX Catch.`
      : 'Remove this run sheet?';
    if (!window.confirm(ask)) return;
    update((p) => ({ ...p, runs: p.runs.filter((r) => r.id !== id) }));
  };
  const printRun = (run) => printPages([runSheetHtml(run, settings)], `Crab Cake Ratios ${longDate(run.date)}`);
  // Confirmed runs live in History; Crab Cake Ratios shows only the runs still being planned
  // or worked on (Carman, Oct 29, 2026).
  const openRuns = (plan.runs || []).filter((r) => !r.confirmed);
  const doneRuns = (plan.runs || []).filter((r) => r.confirmed)
    .sort((a, b) => String(b.date).localeCompare(String(a.date)) || String(b.confirmed.at).localeCompare(String(a.confirmed.at)));
  const printAll = () => {
    const runs = openRuns;
    const pages = [...(printSummary ? [summaryHtml(calc, plan)] : []), ...runs.map((r) => runSheetHtml(r, settings))];
    if (!pages.length) { setErr('Nothing to print yet. Add a run first.'); return; }
    printPages(pages, `Crab Cake Planner ${longDate(todayYmdLocal())}`);
  };

  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-700)', marginBottom: 4 };
  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-700)', borderBottom: '2px solid var(--gray-200)', whiteSpace: 'nowrap', verticalAlign: 'bottom' };
  const td = { padding: '8px 10px', borderBottom: '1px solid var(--gray-100)', verticalAlign: 'top' };
  const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const sub = { fontSize: 11, color: 'var(--gray-600)', fontWeight: 400, whiteSpace: 'nowrap' };
  const box = { width: 90, textAlign: 'right' };
  const modeWord = mode === 'packs' ? 'packs' : mode === 'dozens' ? 'dozen' : 'cakes';

  if (loading) return <div style={{ padding: 24, color: 'var(--gray-600)' }}>Loading…</div>;

  const st = settingsDraft || settings;
  const setSt = (field, v) => setSettingsDraft({ ...(settingsDraft || settings), [field]: v });
  const setIng = (key, field, v) => {
    const cur = settingsDraft || settings;
    setSettingsDraft({ ...cur, ingredients: cur.ingredients.map((i) => (i.key === key ? { ...i, [field]: v } : i)) });
  };
  const setCrabCost = (sku, v) => {
    const cur = settingsDraft || settings;
    setSettingsDraft({ ...cur, crabCosts: { ...(cur.crabCosts || {}), [sku]: v } });
  };

  // The newest QuickBooks price for each ingredient against what the planner uses now.
  // Used in the Ingredients section and in the walkthrough.
  const qbPanel = () => (
    <div>
      {!qbRows ? (
        <button type="button" className="btn btn-primary" onClick={pullQb} disabled={qbBusy}>
          <RefreshCw size={14} style={{ marginRight: 6 }} />{qbBusy ? 'Reading QuickBooks…' : 'Check QuickBooks for the latest prices'}
        </button>
      ) : (
        <>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th} />
                <th style={th}>Ingredient</th>
                <th style={{ ...th, textAlign: 'right' }}>Using now</th>
                <th style={{ ...th, textAlign: 'right' }}>Latest in QuickBooks</th>
                <th style={th}>From</th>
              </tr>
            </thead>
            <tbody>
              {qbFound.map(({ ing, qb }) => (
                <tr key={ing.key}>
                  <td style={td}>
                    <input type="checkbox" disabled={!qb || !editable} checked={!!(qb && qbPick[ing.key])} onChange={(e) => setQbPick((x) => ({ ...x, [ing.key]: e.target.checked }))} aria-label={`Update ${ing.name}`} />
                  </td>
                  <td style={{ ...td, fontWeight: 600 }}>{ing.name}<div style={sub}>{ing.container}</div></td>
                  <td style={{ ...td, ...num }}>{money(ing.price)}</td>
                  <td style={{ ...td, ...num, fontWeight: 700, color: qb && Math.abs(qb.containerPrice - n(ing.price)) >= 0.01 ? 'var(--orange)' : undefined }}>
                    {qb ? money(qb.containerPrice) : <span style={{ color: 'var(--gray-600)', fontWeight: 400 }}>none found</span>}
                  </td>
                  <td style={{ ...td, fontSize: 11, color: 'var(--gray-700)' }}>
                    {qb ? <>{longDate(qb.date)} · {qb.vendor || 'QuickBooks'}<div style={sub}>"{qb.description}"</div></> : 'No bill in the last 18 months with a price on it'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            {editable ? (
              <button type="button" className="btn btn-primary" disabled={!Object.values(qbPick).some(Boolean)} onClick={applyQbPrices}>
                <Save size={14} style={{ marginRight: 6 }} />Update the checked prices
              </button>
            ) : <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>Only Admin, Manager and Procurement can change prices.</span>}
            <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>
              {qbChanged.length ? `${qbChanged.length} price${qbChanged.length === 1 ? ' has' : 's have'} changed.` : 'Every price matches QuickBooks.'}
              {' '}Uncheck any line that isn't what goes in the cakes (a retail case of Old Bay, say).
            </span>
          </div>
        </>
      )}
    </div>
  );

  const stockFigure = (r) => {
    const live = catchStock && catchStock.bySize[r.size.key];
    if (!live) return null;
    return (
      <>
        <div style={{ fontWeight: 600 }}>{fmt(fromPacks(live.total, r.size, mode))} {modeWord}</div>
        <div style={sub}>{allUnits(live.total, r.size)}</div>
        <div style={sub}>{live.locs.map((l) => `${l.loc} ${fmt(l.qty, 0)}`).join(' · ') || 'none in CPX Catch'}</div>
      </>
    );
  };

  // ── The walkthrough (Carman, Oct 27, 2026): prices, what we are making, what we need
  // against what we have, then the planner. Every box here is the same number as on the page.
  const countBox = (key, field, label) => (
    <input
      className="form-input" style={box} type="number" min="0" step="any" aria-label={label}
      value={shown(key, field)}
      onFocus={() => setTyping((t) => ({ ...t, [`${key}.${field}`]: shown(key, field) }))}
      onBlur={() => setTyping((t) => { const c = { ...t }; delete c[`${key}.${field}`]; return c; })}
      onChange={(e) => { const v = e.target.value; setTyping((t) => ({ ...t, [`${key}.${field}`]: v })); setSize(key, field, v); }}
    />
  );
  const INTRO = ['Prices', "What we're making", 'What we need and have', 'Ready'];
  const introBody = () => {
    if (intro === 0) {
      return (
        <>
          <p style={{ marginTop: 0 }}>Do you want me to update the ingredient prices with the most recent prices in QuickBooks?</p>
          {qbPanel()}
        </>
      );
    }
    if (intro === 1) {
      return (
        <>
          <p style={{ marginTop: 0 }}>
            Here's what you're planning to make. Type what you want on hand, in {modeWord}
            (change that with "Enter counts in" at the top of the planner). In stock comes from CPX Catch.
          </p>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Size</th>
                <th style={{ ...th, textAlign: 'right' }}>Want on hand</th>
                <th style={{ ...th, textAlign: 'right' }}>In stock</th>
                <th style={{ ...th, textAlign: 'right' }}>To make</th>
                <th style={{ ...th, textAlign: 'right' }}>Tubs</th>
              </tr>
            </thead>
            <tbody>
              {calc.rows.map((r) => (
                <tr key={r.size.key}>
                  <td style={{ ...td, fontWeight: 700, color: 'var(--navy)' }}>{r.size.label}</td>
                  <td style={{ ...td, ...num }}>{countBox(r.size.key, 'want', `${r.size.label} want on hand`)}</td>
                  <td style={{ ...td, ...num }}>{stockFigure(r) || countBox(r.size.key, 'stock', `${r.size.label} in stock`)}</td>
                  <td style={{ ...td, ...num }}>{fmt(fromPacks(r.toMake, r.size, mode))} {modeWord}<div style={sub}>{allUnits(r.toMake, r.size)}</div></td>
                  <td style={{ ...td, ...num, fontWeight: 700 }}>{r.tubs}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 8 }}>
            To start from what sold, use "Fill from sales" at the top of the planner.
          </div>
        </>
      );
    }
    if (intro === 2) {
      return (
        <>
          <p style={{ marginTop: 0 }}>Here's what the run needs, and what you have on hand. Fill in what's on the shelf.</p>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Item</th>
                <th style={{ ...th, textAlign: 'right' }}>Need</th>
                <th style={{ ...th, textAlign: 'right' }}>Have</th>
                <th style={{ ...th, textAlign: 'right' }}>To order</th>
              </tr>
            </thead>
            <tbody>
              {[['Jumbo lump (lb)', calc.jumboNeeded, 'jumboOnHand', calc.jumboOrder], ['Backfin (lb)', calc.backfinNeeded, 'backfinOnHand', calc.backfinOrder]].map(([name, need, field, order]) => (
                <tr key={field}>
                  <td style={{ ...td, fontWeight: 600 }}>{name}</td>
                  <td style={{ ...td, ...num }}>{fmt(need)}</td>
                  <td style={{ ...td, ...num }}><input className="form-input" style={box} type="number" min="0" step="any" value={plan[field]} onChange={(e) => { const v = e.target.value; update((p) => ({ ...p, [field]: v })); }} aria-label={`${name} on hand`} /></td>
                  <td style={{ ...td, ...num, fontWeight: 700, color: order > 0 ? 'var(--orange)' : 'var(--gray-600)' }}>{fmt(order)}</td>
                </tr>
              ))}
              {calc.ingredients.map((i) => (
                <tr key={i.key}>
                  <td style={{ ...td, fontWeight: 600 }}>{i.name}<div style={sub}>containers: {i.container}</div></td>
                  <td style={{ ...td, ...num }}>{i.missing ? '—' : i.containers}</td>
                  <td style={{ ...td, ...num }}><input className="form-input" style={{ ...box, width: 70 }} type="number" min="0" step="any" value={plan.ingredientsOnHand[i.key] ?? ''} onChange={(e) => { const v = e.target.value; update((p) => ({ ...p, ingredientsOnHand: { ...p.ingredientsOnHand, [i.key]: v } })); }} aria-label={`${i.name} containers on hand`} /></td>
                  <td style={{ ...td, ...num, fontWeight: 700, color: i.order > 0 ? 'var(--orange)' : 'var(--gray-600)' }}>{i.order}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    }
    return (
      <>
        <p style={{ marginTop: 0 }}>That's the plan.</p>
        <div style={{ fontSize: 14, lineHeight: 1.8 }}>
          <div><b>{fmt(calc.tubsLeft, 0)}</b> tubs to make, <b>{fmt(calc.packsLeft)}</b> packs, about <b>{fmt(calc.crewHours)}</b> crew hours</div>
          <div><b>{money(calc.cashToOrder)}</b> to order: {money(calc.crabOrderCost)} crab and {money(calc.ingOrderCost)} ingredients</div>
          <div><b>{money(calc.totalRun)}</b> total run cost, labor {calc.laborActual ? 'from the time clock' : 'estimated'}</div>
        </div>
        {calc.tubsLeft > 0 && !openRuns.length ? (
          <button type="button" className="btn btn-outline" style={{ marginTop: 12 }} onClick={() => { addRunsFromPlan(); closeIntro(); setOpen({ ...openMap, ratios: true }); }}>
            <Plus size={14} style={{ marginRight: 4 }} />Make the run sheets
          </button>
        ) : null}
      </>
    );
  };

  return (
    <div>
      {intro != null ? (
        <div role="dialog" aria-modal="true" aria-label="Crab Cake Planner walkthrough" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' }}>
          <div className="card" style={{ width: '100%', maxWidth: 820, margin: 0 }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span>Plan the run</span>
              <span style={{ flex: 1 }} />
              {INTRO.map((t, i) => (
                <span key={t} style={{ fontSize: 11, fontWeight: i === intro ? 700 : 400, color: i === intro ? 'var(--navy)' : 'var(--gray-600)', textTransform: 'none' }}>
                  {i + 1}. {t}
                </span>
              ))}
            </div>
            <div className="card-body" style={{ fontSize: 13, overflowX: 'auto' }}>{introBody()}</div>
            <div className="card-body" style={{ display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" defaultChecked={(() => { try { return localStorage.getItem('ccp.introOff') !== '1'; } catch { return true; } })()} onChange={(e) => setIntroOff(!e.target.checked)} />
                Show this when I open the planner
              </label>
              <span style={{ flex: 1 }} />
              <button type="button" className="btn btn-ghost" onClick={closeIntro}>Skip to the planner</button>
              {intro > 0 ? <button type="button" className="btn btn-outline" onClick={() => setIntro(intro - 1)}>Back</button> : null}
              {intro < INTRO.length - 1
                ? <button type="button" className="btn btn-primary" onClick={() => setIntro(intro + 1)}>Next</button>
                : <button type="button" className="btn btn-primary" onClick={closeIntro}>Open the planner</button>}
            </div>
          </div>
        </div>
      ) : null}
      <div className="breadcrumb">
        <a href="#procurement">Procurement</a>
        <span> / </span>
        <span>Crab Cake Planner</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--navy)' }}>Crab Cake Planner</h1>
          <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
            How many tubs to make, and the crab and ingredients to order for them.
            {saveState ? <span style={{ marginLeft: 8, color: '#15803d', fontWeight: 600 }}>{saveState}</span> : null}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary" onClick={() => setIntro(0)}>Walk me through it</button>
          <button type="button" className="btn btn-outline" onClick={() => setAll(true)}>Expand all</button>
          <button type="button" className="btn btn-outline" onClick={() => setAll(false)}>Collapse all</button>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-700)', marginLeft: 8 }}>Enter counts in</span>
          <div style={{ display: 'inline-flex', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
            {MODES.map(([v, l]) => (
              <button
                key={v} type="button" onClick={() => update((p) => ({ ...p, mode: v }))} aria-pressed={mode === v}
                style={{
                  padding: '6px 14px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                  background: mode === v ? 'var(--navy)' : 'white', color: mode === v ? 'white' : 'var(--gray-700)',
                }}
              >{l}</button>
            ))}
          </div>
        </div>
      </div>

      {err ? (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--orange)' }}>
          <div className="card-body" style={{ color: 'var(--orange)' }}>{err}</div>
        </div>
      ) : null}

      {/* ── Fill "want on hand" from sales ─────────────────────────────── */}
      <Section id="sales" title='Fill "want on hand" from sales' open={isOpen('sales')} onToggle={toggle} style={{ borderTop: '3px solid var(--orange)' }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={lbl} htmlFor="cc-from">From</label>
            <input id="cc-from" type="date" className="form-input" value={plan.salesFrom} onChange={(e) => update((p) => ({ ...p, salesFrom: e.target.value }))} />
          </div>
          <div>
            <label style={lbl} htmlFor="cc-to">To</label>
            <input id="cc-to" type="date" className="form-input" value={plan.salesTo} onChange={(e) => update((p) => ({ ...p, salesTo: e.target.value }))} />
          </div>
          <button type="button" className="btn btn-primary" onClick={fillFromSales} disabled={salesBusy}>
            <RefreshCw size={14} style={{ marginRight: 6 }} />{salesBusy ? 'Looking up…' : 'Fill from sales'}
          </button>
          <div style={{ fontSize: 12, color: 'var(--gray-600)', flex: '1 1 260px' }}>
            Units shipped in that range, from the CPx product list. Sales come back in dozens and are converted to packs.
            You can still type over any number.
          </div>
        </div>
      </Section>

      {/* ── By size ─────────────────────────────────────────────────────── */}
      <Section id="sizes" title="By size" extra={<span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-600)' }}>· boxes take {modeWord}</span>} open={isOpen('sizes')} onToggle={toggle}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Size</th>
                <th style={{ ...th, textAlign: 'right' }}>Want on hand<div style={sub}>{modeWord}</div></th>
                <th style={{ ...th, textAlign: 'right' }}>In stock<div style={sub}>{catchStock ? `CPX Catch, ${catchStock.at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : modeWord}</div></th>
                <th style={{ ...th, textAlign: 'right' }}>To make</th>
                <th style={{ ...th, textAlign: 'right' }}>Tubs</th>
                <th style={{ ...th, textAlign: 'right' }}>Will make</th>
                <th style={{ ...th, textAlign: 'right' }}>Jumbo lb</th>
                <th style={{ ...th, textAlign: 'right' }}>Backfin lb</th>
                <th style={{ ...th, textAlign: 'right' }}>Cost per pack<div style={sub}>no labor · with labor</div></th>
              </tr>
            </thead>
            <tbody>
              {calc.rows.map((r) => (
                <tr key={r.size.key}>
                  <td style={{ ...td, fontWeight: 700, color: 'var(--navy)' }}>
                    {r.size.label}
                    <div style={sub}>{r.size.cakesPerPack} cakes a pack · {fmt(r.lbPerPack, 2)} lb · {fmt(r.packsPerTub, 2)} packs a tub</div>
                  </td>
                  <td style={{ ...td, ...num }}>
                    <input className="form-input" style={box} type="number" min="0" step="any" value={shown(r.size.key, 'want')} onFocus={() => setTyping((t) => ({ ...t, [`${r.size.key}.want`]: shown(r.size.key, 'want') }))} onBlur={() => setTyping((t) => { const c = { ...t }; delete c[`${r.size.key}.want`]; return c; })} onChange={(e) => { const v = e.target.value; setTyping((t) => ({ ...t, [`${r.size.key}.want`]: v })); setSize(r.size.key, 'want', v); }} aria-label={`${r.size.label} want on hand`} />
                    <div style={sub}>{allUnits(r.want, r.size)}</div>
                    {plan.salesDozens ? <div style={sub}>sold {fmt(plan.salesDozens[r.size.key])} dz</div> : null}
                  </td>
                  <td style={{ ...td, ...num }}>
                    {stockFigure(r) || (
                      <>
                        <input className="form-input" style={box} type="number" min="0" step="any" value={shown(r.size.key, 'stock')} onFocus={() => setTyping((t) => ({ ...t, [`${r.size.key}.stock`]: shown(r.size.key, 'stock') }))} onBlur={() => setTyping((t) => { const c = { ...t }; delete c[`${r.size.key}.stock`]; return c; })} onChange={(e) => { const v = e.target.value; setTyping((t) => ({ ...t, [`${r.size.key}.stock`]: v })); setSize(r.size.key, 'stock', v); }} aria-label={`${r.size.label} in stock`} />
                        <div style={sub}>{allUnits(r.stock, r.size)}</div>
                      </>
                    )}
                  </td>
                  <td style={{ ...td, ...num, fontWeight: 600 }}>
                    {fmt(fromPacks(r.toMake, r.size, mode))} {modeWord}
                    <div style={sub}>{allUnits(r.toMake, r.size)}</div>
                  </td>
                  <td style={{ ...td, ...num, fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{r.tubs}</td>
                  <td style={{ ...td, ...num }}>
                    {fmt(fromPacks(r.willMake, r.size, mode))} {modeWord}
                    <div style={sub}>{allUnits(r.willMake, r.size)}</div>
                    {r.extra > 0.001 ? <div style={{ ...sub, color: '#b45309' }}>+{fmt(fromPacks(r.extra, r.size, mode))} {modeWord} over</div> : null}
                  </td>
                  <td style={{ ...td, ...num }}>{fmt(r.jumboLb)}</td>
                  <td style={{ ...td, ...num }}>{fmt(r.backfinLb)}</td>
                  <td style={{ ...td, ...num }}>
                    {money(r.costPerPack)}
                    <div style={sub}>{money(r.costWithLabor)} with labor</div>
                    <div style={{ ...sub, color: r.avg ? '#15803d' : undefined }}>{r.avg ? `average of ${r.avg.runs} run${r.avg.runs === 1 ? '' : 's'}` : 'estimate, no runs yet'}</div>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Carman, Oct 20, 2026: total the crab columns and net off what is on hand,
                so the last row is exactly the jumbo and backfin to order. The on-hand
                boxes are the same numbers as in the Crab section below. */}
            <tfoot>
              <tr>
                <td colSpan={6} style={{ ...td, fontWeight: 700, borderTop: '2px solid var(--gray-200)' }}>Total crab needed</td>
                <td style={{ ...td, ...num, fontWeight: 700, borderTop: '2px solid var(--gray-200)' }}>{fmt(calc.jumboNeeded)}</td>
                <td style={{ ...td, ...num, fontWeight: 700, borderTop: '2px solid var(--gray-200)' }}>{fmt(calc.backfinNeeded)}</td>
                <td style={{ ...td, borderTop: '2px solid var(--gray-200)' }} />
              </tr>
              <tr>
                <td colSpan={6} style={td}>Less crab on hand (lb)</td>
                {['jumboOnHand', 'backfinOnHand'].map((field) => (
                  <td key={field} style={{ ...td, ...num }}>
                    <input className="form-input" style={{ ...box, width: 80 }} type="number" min="0" step="any" value={plan[field]} onChange={(e) => { const v = e.target.value; update((p) => ({ ...p, [field]: v })); }} aria-label={field === 'jumboOnHand' ? 'Jumbo lump on hand' : 'Backfin on hand'} />
                  </td>
                ))}
                <td style={td} />
              </tr>
              <tr>
                <td colSpan={6} style={{ ...td, fontWeight: 700, color: 'var(--navy)' }}>Crab to order</td>
                {[calc.jumboOrder, calc.backfinOrder].map((v, i) => (
                  <td key={i} style={{ ...td, ...num, fontWeight: 700, fontSize: 14, color: v > 0 ? 'var(--orange)' : 'var(--gray-600)' }}>{fmt(v)}</td>
                ))}
                <td style={td} />
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="card-body" style={{ fontSize: 12, color: 'var(--gray-600)', paddingTop: 8 }}>
          Crab pounds and the totals below are for the tubs to make. Once a size has confirmed runs, its cost per pack is
          their average: every run's crab, ingredients, packaging and labor added up, divided by every pack produced (see Actual cost
          by run). Before that it's the crab and ingredients in one tub, divided by the packs a tub makes, plus the
          {' '}{money(settings.packagingPerPack)} bag. Labor is {money(calc.laborPerPack)} a pack
          {calc.laborActual ? ', actual, from the time clock' : ', estimated from the crew rate in settings'}.
          {' '}In stock comes from CPX Catch, every location added up. A run counts once it's confirmed: In stock goes up, and To make and the crab pounds come down.
          {stockErr ? <div style={{ color: 'var(--orange)', fontWeight: 600, marginTop: 6 }}>Could not read CPX Catch ({stockErr}). In stock is showing the last numbers typed in here.</div> : null}
        </div>
      </Section>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* ── Crab ──────────────────────────────────────────────────────── */}
        <Section id="crab" title="Crab" open={isOpen('crab')} onToggle={toggle} style={{ flex: '1 1 340px', minWidth: 0 }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Grade</th>
                <th style={{ ...th, textAlign: 'right' }}>Needed lb</th>
                <th style={{ ...th, textAlign: 'right' }}>On hand lb</th>
                <th style={{ ...th, textAlign: 'right' }}>To order lb</th>
                <th style={{ ...th, textAlign: 'right' }}>Order cost</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Jumbo lump', calc.jumboNeeded, 'jumboOnHand', calc.jumboOrder, calc.jumboPrice, settings.jumboSku],
                ['Backfin', calc.backfinNeeded, 'backfinOnHand', calc.backfinOrder, calc.backfinPrice, settings.backfinSku],
              ].map(([name, need, field, order, price, sku]) => (
                <tr key={field}>
                  <td style={{ ...td, fontWeight: 600 }}>{name}<div style={sub}>{money(price)}/lb{sku && n((settings.crabCosts || {})[sku]) > 0 ? ` · ${(CRAB_SKUS.find((c) => c.sku === sku) || {}).name || sku}` : ''}</div></td>
                  <td style={{ ...td, ...num }}>{fmt(need)}</td>
                  <td style={{ ...td, ...num }}>
                    <input className="form-input" style={box} type="number" min="0" step="any" value={plan[field]} onChange={(e) => { const v = e.target.value; update((p) => ({ ...p, [field]: v })); }} aria-label={`${name} on hand`} />
                  </td>
                  <td style={{ ...td, ...num, fontWeight: 700, color: order > 0 ? 'var(--orange)' : 'var(--gray-600)' }}>{fmt(order)}</td>
                  <td style={{ ...td, ...num }}>{money(order * n(price))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {/* ── Totals ────────────────────────────────────────────────────── */}
        <Section id="run" title="The run" open={isOpen('run')} onToggle={toggle} style={{ flex: '1 1 300px', minWidth: 0 }}>
          <div className="card-body" style={{ fontSize: 13 }}>
            {[
              ['Tubs to make', fmt(calc.tubsLeft, 0)],
              ['Packs from those tubs', fmt(calc.packsLeft)],
              ['Crew hours', fmt(calc.crewHours)],
              null,
              ['Crab used', money(calc.crabUsed)],
              ['Ingredients used', money(calc.ingUsed)],
              ['Packaging', money(calc.packaging)],
              ['Labor', money(calc.labor)],
              ['Total run cost', money(calc.totalRun), true],
              null,
              ['Crab to order', money(calc.crabOrderCost)],
              ['Ingredients to order', money(calc.ingOrderCost)],
              ['Cash to order', money(calc.cashToOrder), true],
            ].map((row, i) => (row ? (
              <div key={row[0]} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontWeight: row[2] ? 700 : 400, color: row[2] ? 'var(--navy)' : undefined, borderTop: row[2] ? '1px solid var(--gray-200)' : undefined }}>
                <span>{row[0]}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{row[1]}</span>
              </div>
            ) : <div key={`gap${i}`} style={{ height: 8 }} />))}
          </div>
        </Section>
      </div>

      {/* ── Ingredients ─────────────────────────────────────────────────── */}
      <Section id="ingredients" title="Ingredients" extra={<span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-600)' }}>· on hand is in containers</span>} open={isOpen('ingredients')} onToggle={toggle}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Ingredient</th>
                <th style={th}>Per batch · per tub</th>
                <th style={{ ...th, textAlign: 'right' }}>Needed</th>
                <th style={{ ...th, textAlign: 'right' }}>Containers</th>
                <th style={{ ...th, textAlign: 'right' }}>On hand</th>
                <th style={{ ...th, textAlign: 'right' }}>To order</th>
                <th style={{ ...th, textAlign: 'right' }}>Order cost</th>
              </tr>
            </thead>
            <tbody>
              {calc.ingredients.map((i) => (
                <tr key={i.key}>
                  <td style={{ ...td, fontWeight: 600 }}>{i.name}<div style={sub}>{i.container} · {money(i.price)}{i.priceFrom ? ` · ${i.priceFrom}` : ''}</div></td>
                  <td style={td}>{i.perBatch} · {i.tubMeasure}<div style={sub}>{fmt(i.perTub, 2)} {i.unit} a tub</div></td>
                  <td style={{ ...td, ...num }}>{fmt(i.amount)} {i.unit}</td>
                  <td style={{ ...td, ...num }}>{i.missing ? <span style={{ color: '#b45309' }}>set size and price</span> : i.containers}</td>
                  <td style={{ ...td, ...num }}>
                    <input className="form-input" style={{ ...box, width: 70 }} type="number" min="0" step="any" value={plan.ingredientsOnHand[i.key] ?? ''} onChange={(e) => { const v = e.target.value; update((p) => ({ ...p, ingredientsOnHand: { ...p.ingredientsOnHand, [i.key]: v } })); }} aria-label={`${i.name} containers on hand`} />
                  </td>
                  <td style={{ ...td, ...num, fontWeight: 700, color: i.order > 0 ? 'var(--orange)' : 'var(--gray-600)' }}>{i.order}</td>
                  <td style={{ ...td, ...num }}>{money(i.orderCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card-body" style={{ fontSize: 12, color: 'var(--gray-600)', paddingTop: 8 }}>
          A tub is {settings.batchesPerTub} batches of {fmt(n(settings.crabPerTub) / n(settings.batchesPerTub || 1))} lb crab.
          Ingredients cost {money(calc.ingCostPerTub)} a tub.
        </div>
        <div className="card-body" style={{ borderTop: '1px solid var(--gray-100)' }}>
          <div style={{ ...lbl, marginBottom: 8 }}>Prices from QuickBooks</div>
          {qbPanel()}
        </div>
      </Section>

      {/* ── Actual labor and cost by run (Carman, Oct 29, 2026) ─────────── */}
      <Section id="labor" title="Actual cost by run" extra={<span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-600)' }}>· confirmed runs, labor from the time clock</span>} open={isOpen('labor')} onToggle={toggle}>
        <div className="card-body" style={{ fontSize: 13 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--gray-600)', flex: '1 1 320px' }}>
              Labor is the hours clocked to the Crab Cakes job code on each run's date, at each person's payroll rate (overtime at
              time and a half) plus the employer's payroll taxes. When two runs are made the same day, the day's labor is split by
              packs produced. Only confirmed runs count.
            </div>
            <button type="button" className="btn btn-outline" onClick={loadLabor} disabled={laborBusy}>
              <RefreshCw size={14} style={{ marginRight: 6 }} />{laborBusy ? 'Reading the time clock…' : 'Refresh from the time clock'}
            </button>
          </div>
          {(() => {
            const done = (plan.runs || []).filter((r) => costs.byRun[r.id]).sort((a, b) => String(a.date).localeCompare(String(b.date)));
            if (!done.length) {
              return <div style={{ color: 'var(--gray-600)' }}>No confirmed runs yet. Press "Confirm done" on a run below once it's made. Until then the planner uses the estimates in settings.</div>;
            }
            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Run</th>
                      <th style={{ ...th, textAlign: 'right' }}>Packs</th>
                      <th style={{ ...th, textAlign: 'right' }}>Crab, ingredients, packaging</th>
                      <th style={{ ...th, textAlign: 'right' }}>Labor</th>
                      <th style={{ ...th, textAlign: 'right' }}>This run a pack</th>
                      <th style={{ ...th, textAlign: 'right' }}>Average a pack</th>
                    </tr>
                  </thead>
                  <tbody>
                    {done.map((r) => {
                      const c = costs.byRun[r.id];
                      const a = costs.bySize[r.sizeKey];
                      const size = SIZES.find((s) => s.key === r.sizeKey) || SIZES[0];
                      const day = laborDays && laborDays.days ? laborDays.days[r.date] : null;
                      const diff = a ? c.perPack - a.perPack : 0;
                      return (
                        <tr key={r.id}>
                          <td style={{ ...td, fontWeight: 600 }}>{size.label} · {longDate(r.date)}<div style={sub}>{r.confirmed.batch ? `CPX Catch ${r.confirmed.batch}` : ''}</div></td>
                          <td style={{ ...td, ...num }}>{fmt(c.packs, 0)}</td>
                          <td style={{ ...td, ...num }}>{money(c.material)}{c.noCost.length ? <div style={{ ...sub, color: '#b45309' }}>no $/lb for {c.noCost.map((x) => x.sku).join(', ')}</div> : null}</td>
                          <td style={{ ...td, ...num }}>
                            {day ? money(c.labor) : <span style={{ color: '#b45309' }}>{laborDays ? 'no hours clocked' : '…'}</span>}
                            {day ? <div style={sub}>{fmt(c.hours, 2)} hr{c.sharedWith ? ` · split with ${c.sharedWith} other run${c.sharedWith === 1 ? '' : 's'}` : ''}</div> : null}
                          </td>
                          <td style={{ ...td, ...num, fontWeight: 700 }}>
                            {money(c.perPack)}
                            {a && a.runs > 1 && Math.abs(diff) >= 0.01 ? <div style={{ ...sub, color: diff > 0 ? '#b00020' : '#15803d' }}>{diff > 0 ? '+' : '-'}{money(Math.abs(diff))} vs average</div> : null}
                          </td>
                          <td style={{ ...td, ...num, fontWeight: 700, color: 'var(--navy)' }}>{a ? money(a.perPack) : ''}<div style={sub}>{a ? `${a.runs} run${a.runs === 1 ? '' : 's'}, ${fmt(a.packs, 0)} packs` : ''}</div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginTop: 12 }}>
            <div>
              <div style={lbl}>Labor per pack</div>
              <b style={{ fontSize: 16, color: 'var(--navy)' }}>{labor.actual != null ? money(labor.actual) : '—'}</b>
              <div style={sub}>{labor.actual != null ? `actual, all confirmed runs · estimate was ${money(labor.estimate)}` : `using the estimate, ${money(labor.estimate)}`}</div>
            </div>
          </div>
          {laborDays && laborDays.unpriced && laborDays.unpriced.length ? (
            <div style={{ marginTop: 8, color: '#b45309', fontSize: 12 }}>
              No payroll rate on file for {laborDays.unpriced.join(', ')} ({fmt(laborDays.unpricedHours, 2)} hours), so those hours are not in the cost. Tell Mercedes.
            </div>
          ) : null}
          {laborDays && laborDays.pulledAt ? (
            <div style={{ ...sub, marginTop: 6, whiteSpace: 'normal' }}>
              Payroll rates as of {longDate(laborDays.ratesAsOf)}, plus {fmt(n(laborDays.employerShare) * 100, 1)}% employer taxes. Read from the time clock {new Date(laborDays.pulledAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.
            </div>
          ) : null}
        </div>
      </Section>

      {/* ── Crab Cake Ratios (replaces the Crab Meat Log sheet) ─────────── */}
      <Section id="ratios" title="Crab Cake Ratios" extra={<span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-600)' }}>· one sheet per production run</span>} open={isOpen('ratios')} onToggle={toggle}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <button type="button" className="btn btn-primary" onClick={addRunsFromPlan}><Plus size={14} style={{ marginRight: 4 }} />Add runs from plan</button>
            <button type="button" className="btn btn-outline" onClick={addRun}><Plus size={14} style={{ marginRight: 4 }} />Add a run</button>
            <span style={{ flex: 1 }} />
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={printSummary} onChange={(e) => setPrintSummary(e.target.checked)} />
              Planner summary as the first page
            </label>
            <button type="button" className="btn btn-outline" onClick={printAll}><Printer size={14} style={{ marginRight: 6 }} />Print all</button>
          </div>
          {!openRuns.length ? (
            <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>
              No runs in progress. "Add runs from plan" makes one for each size that still has tubs left.
              {doneRuns.length ? ' Confirmed runs are in History, under Settings.' : ''}
            </div>
          ) : null}
          {openRuns.map((run) => {
            const f = runFigures(run, settings);
            const locked = !!run.confirmed;
            const c = costs.byRun[run.id];
            const avg = costs.bySize[run.sizeKey];
            const status = (planned, target, isOff) => (
              <span style={{ fontWeight: 700, color: !f.anyEntered ? 'var(--gray-600)' : isOff ? '#b00020' : '#15803d' }}>
                {fmt(planned, 2)} of {fmt(target, 2)} lb{f.anyEntered ? (isOff ? ` · off by ${fmt(planned - target, 2)}` : ' · matches') : ''}
              </span>
            );
            return (
              <div key={run.id} style={{ border: `1px solid ${f.jumboOff || f.backfinOff ? '#f3b4bd' : 'var(--gray-200)'}`, borderRadius: 8, padding: 12, marginBottom: 12, background: f.jumboOff || f.backfinOff ? '#fff6f7' : 'white' }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label style={lbl}>Size</label>
                    <select className="form-select" disabled={locked} value={run.sizeKey} onChange={(e) => setRun(run.id, 'sizeKey', e.target.value)}>
                      {SIZES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>12 lb batches</label>
                    <input className="form-input" style={{ width: 90 }} type="number" min="0" step="1" disabled={locked} value={run.batches} onChange={(e) => setRun(run.id, 'batches', e.target.value)} />
                    <div style={sub}>{fmt(f.tubs, 2)} tub{f.tubs === 1 ? '' : 's'}</div>
                  </div>
                  <div>
                    <label style={lbl}>Date</label>
                    <input className="form-input" type="date" disabled={locked} value={run.date || ''} onChange={(e) => setRun(run.id, 'date', e.target.value)} />
                  </div>
                  <div style={{ fontSize: 12 }}>
                    <div><b>Jumbo target</b> {fmt(f.jumboTarget, 2)} lb</div>
                    <div><b>Backfin target</b> {fmt(f.backfinTarget, 2)} lb</div>
                  </div>
                  <div>
                    <label style={lbl}>Packs produced</label>
                    <input className="form-input" style={{ width: 90 }} type="number" min="0" step="1" disabled={locked} value={run.packsMade ?? ''} onChange={(e) => { setRunErr(null); setRun(run.id, 'packsMade', e.target.value); }} aria-label="Packs produced on this run" />
                    <div style={sub}>planned {fmt(f.packs, 0)}</div>
                  </div>
                  <div>
                    <label style={lbl}>Loose cakes</label>
                    <input className="form-input" style={{ width: 70 }} type="number" min="0" max={f.size.cakesPerPack - 1} step="1" disabled={locked} placeholder="0" value={run.loose ?? ''} onChange={(e) => setRun(run.id, 'loose', e.target.value)} aria-label="Loose cakes left over" />
                    <div style={sub}>left over</div>
                  </div>
                  <div style={{ fontSize: 12 }}>
                    <div><b>Crab cost</b> {f.crabCost > 0 ? money(f.crabCost) : '—'}</div>
                    {c ? (
                      <>
                        <div><b>This run</b> {money(c.perPack)} a pack<span style={sub}> with labor</span></div>
                        {avg ? <div><b>Average</b> {money(avg.perPack)} a pack<span style={sub}> {avg.runs} run{avg.runs === 1 ? '' : 's'}</span></div> : null}
                      </>
                    ) : (
                      <div><b>Cost per pack</b> {f.costPerPack != null ? money(f.costPerPack) : '—'}<span style={sub}> planned, no labor</span></div>
                    )}
                    {f.noCost.length ? <div style={{ color: '#b45309' }}>No $/lb set for {f.noCost.map((x) => x.sku).join(', ')}</div> : null}
                  </div>
                  <div>
                    {locked ? (
                      <div style={{ fontSize: 12, color: '#15803d', fontWeight: 700 }}>
                        Done · {fmt(run.confirmed.packs, 0)} packs
                        <div style={{ ...sub, color: '#15803d' }}>in CPX Catch, {run.confirmed.batch}</div>
                        {run.confirmed.flagged ? <div style={{ ...sub, color: '#b45309' }}>yield {run.confirmed.pct}% of expected, flagged</div> : null}
                      </div>
                    ) : (
                      <button type="button" className="btn btn-primary" disabled={confirming === run.id} onClick={() => confirmRun(run)}>
                        {confirming === run.id ? 'Posting…' : 'Confirm done'}
                      </button>
                    )}
                  </div>
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn btn-outline" onClick={() => printRun(run)}><Printer size={14} style={{ marginRight: 6 }} />Print this sheet</button>
                  <button type="button" className="btn btn-ghost" onClick={() => removeRun(run.id)} title="Remove this run" aria-label="Remove this run"><Trash2 size={14} /></button>
                </div>
                {!locked ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10, fontSize: 12 }}>
                    <span style={{ ...lbl, marginBottom: 0 }}>Finished units go to</span>
                    <span>
                      {(catchStock && catchStock.bySize[run.sizeKey] && catchStock.bySize[run.sizeKey].home) || 'F02'} gets
                      {' '}{n(run.packsMade) > 0 ? fmt(n(run.packsMade) - (run.putAway || []).reduce((sum, x) => sum + n(x.qty), 0), 0) : 'the packs'}
                      {(run.putAway || []).length ? ' (the rest)' : ''}
                    </span>
                    {(run.putAway || []).map((x, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <select className="form-input" style={{ width: 190 }} value={x.loc} aria-label="Other location" onChange={(e) => setRun(run.id, 'putAway', run.putAway.map((y, j) => (j === i ? { ...y, loc: e.target.value } : y)))}>
                          {PUT_AWAY_LOCS.map((k) => <option key={k} value={k}>{k} — {LOCATIONS[k]}</option>)}
                        </select>
                        <input className="form-input" style={{ width: 70 }} type="number" min="0" step="1" placeholder="packs" value={x.qty} aria-label={`Packs going to ${x.loc}`} onChange={(e) => { setRunErr(null); setRun(run.id, 'putAway', run.putAway.map((y, j) => (j === i ? { ...y, qty: e.target.value } : y))); }} />
                        <button type="button" className="btn btn-ghost" onClick={() => { setRunErr(null); setRun(run.id, 'putAway', run.putAway.filter((y, j) => j !== i)); }} title="Remove this location" aria-label="Remove this location"><Trash2 size={14} /></button>
                      </span>
                    ))}
                    <button type="button" className="btn btn-outline" onClick={() => setRun(run.id, 'putAway', [...(run.putAway || []), { loc: 'F01', qty: '' }])}><Plus size={14} style={{ marginRight: 4 }} />Another location</button>
                  </div>
                ) : null}
                {runErr && runErr.id === run.id ? (
                  <div role="alert" style={{ marginTop: 10, padding: '8px 12px', borderLeft: '4px solid var(--orange)', color: 'var(--orange)', fontSize: 13, fontWeight: 600 }}>{runErr.msg}</div>
                ) : null}
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
                  {['jumbo', 'backfin'].map((grade) => (
                    <div key={grade} style={{ flex: '1 1 280px' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--navy)', marginBottom: 6 }}>
                        {grade === 'jumbo' ? 'Jumbo' : 'Backfin'} · planned lb
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {CRAB_SKUS.filter((c) => c.grade === grade).map((c) => (
                          <label key={c.sku} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                            <span style={{ flex: 1 }}>{c.name}<span style={{ ...sub, marginLeft: 6 }}>{c.sku}</span></span>
                            <input className="form-input" style={{ width: 80, textAlign: 'right' }} type="number" min="0" step="any" disabled={locked} value={run.lbs?.[c.sku] ?? ''} onChange={(e) => setRunLb(run.id, c.sku, e.target.value)} aria-label={`${c.name} (${c.sku}) planned pounds`} />
                          </label>
                        ))}
                      </div>
                      <div style={{ fontSize: 12, marginTop: 6 }}>
                        {grade === 'jumbo' ? status(f.jumboPlanned, f.jumboTarget, f.jumboOff) : status(f.backfinPlanned, f.backfinTarget, f.backfinOff)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 12, color: 'var(--gray-600)' }}>
            A batch is {fmt(n(settings.crabPerTub) / (n(settings.batchesPerTub) || 1), 2)} lb of crab: 3 lb jumbo and 9 lb backfin for regular cakes, all jumbo for All Jumbo.
            The printed sheet lists only the crab meat with pounds entered, or every SKU blank if none are.
          </div>
        </div>
      </Section>

      {/* ── Settings ────────────────────────────────────────────────────── */}
      <Section
        id="settings" open={isOpen('settings')} onToggle={toggle}
        title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Gear size={14} /> Settings</span>}
        extra={meta.settings?.updatedAt ? (
          <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-600)', marginLeft: 6 }}>
            last changed {longDate(String(meta.settings.updatedAt).slice(0, 10))}{meta.settings.updatedBy ? ` by ${meta.settings.updatedBy}` : ''}
          </span>
        ) : null}
      >
        <div className="card-body">
          {!editable ? <div style={{ fontSize: 12, color: 'var(--gray-600)', marginBottom: 10 }}>Only Admin, Manager and Procurement can change these.</div> : null}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            {[
              ['crabPerTub', 'Crab per tub (lb)'],
              ['mixPerTub', 'Finished mix per tub (lb)'],
              ['batchesPerTub', 'Batches per tub'],
              ['jumboPrice', 'Jumbo $/lb if no SKU'],
              ['backfinPrice', 'Backfin $/lb if no SKU'],
              ['packsPerCrewHour', 'Packs per crew hour'],
              ['crewRate', 'Crew labor $/hr'],
              ['packagingPerPack', 'Packaging $/pack'],
            ].map(([f, l]) => (
              <div key={f} style={{ flex: '0 0 150px' }}>
                <label style={lbl} htmlFor={`cc-${f}`}>{l}</label>
                <input id={`cc-${f}`} className="form-input" type="number" step="any" min="0" disabled={!editable} value={st[f]} onChange={(e) => setSt(f, e.target.value)} />
              </div>
            ))}
          </div>
          {/* Crab meat cost by SKU (Carman, Oct 27, 2026): each meat costs something different. */}
          <div style={{ ...lbl, marginBottom: 6 }}>Crab meat cost by SKU</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
            {['jumbo', 'backfin'].map((grade) => {
              const skuField = grade === 'jumbo' ? 'jumboSku' : 'backfinSku';
              return (
                <div key={grade} style={{ flex: '1 1 320px' }}>
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>{grade === 'jumbo' ? 'Jumbo' : 'Backfin'}</th>
                        <th style={{ ...th, textAlign: 'right' }}>$/lb</th>
                        <th style={{ ...th, textAlign: 'center' }}>Plan with</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CRAB_SKUS.filter((c) => c.grade === grade).map((c) => (
                        <tr key={c.sku}>
                          <td style={td}>{c.name}<span style={{ ...sub, marginLeft: 6 }}>{c.sku}</span></td>
                          <td style={{ ...td, ...num }}>
                            <input className="form-input" style={{ width: 80, textAlign: 'right' }} type="number" step="any" min="0" disabled={!editable} value={(st.crabCosts || {})[c.sku] ?? ''} onChange={(e) => setCrabCost(c.sku, e.target.value)} aria-label={`${c.name} cost per pound`} />
                          </td>
                          <td style={{ ...td, textAlign: 'center' }}>
                            <input type="radio" name={`cc-${skuField}`} disabled={!editable} checked={st[skuField] === c.sku} onChange={() => setSt(skuField, c.sku)} aria-label={`Plan ${grade} with ${c.name}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-600)', marginBottom: 16 }}>
            Run sheets cost each run from the meat entered on it. The planner's totals use the "plan with" meat for each grade,
            or the $/lb above if that meat has no cost yet.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Ingredient', 'Per batch', 'Per tub (as measured)', 'Per tub (container unit)', 'Unit', 'Container', 'Container size', 'Container price', 'Billed as'].map((h) => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {st.ingredients.map((i) => (
                  <tr key={i.key}>
                    <td style={{ ...td, fontWeight: 600 }}>{i.name}</td>
                    {[['perBatch', 'text', 110], ['tubMeasure', 'text', 110], ['perTub', 'number', 90], ['unit', 'text', 70], ['container', 'text', 130], ['containerSize', 'number', 90], ['price', 'number', 90], ['billedAs', 'text', 80]].map(([f, t, w]) => (
                      <td key={f} style={td}>
                        <input className="form-input" style={{ width: w }} type={t} step="any" min="0" disabled={!editable} value={i[f]} onChange={(e) => setIng(i.key, f, e.target.value)} aria-label={`${i.name} ${f}`} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editable ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
              <button type="button" className="btn btn-primary" disabled={!settingsDraft} onClick={saveSettings}>
                <Save size={14} style={{ marginRight: 6 }} />Save settings
              </button>
              {settingsDraft ? <button type="button" className="btn btn-outline" onClick={() => setSettingsDraft(null)}>Cancel</button> : null}
              <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>
                Per tub (container unit) is what the math uses, in the same unit as the container size.
                Billed as is the container in QuickBooks' units (25 lb, 4 gal, 360 ea), so a bill's $/lb becomes a container price.
              </span>
            </div>
          ) : null}
        </div>
      </Section>

      {/* ── History: confirmed runs (Carman, Oct 29, 2026) ──────────────── */}
      <Section id="history" title="History" extra={<span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-600)' }}>· confirmed runs, newest first</span>} open={isOpen('history')} onToggle={toggle}>
        <div className="card-body">
          {!doneRuns.length ? (
            <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>No confirmed runs yet. A run moves here when you press "Confirm done" on it.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Date</th>
                    <th style={th}>Size</th>
                    <th style={th}>CPX Catch batch</th>
                    <th style={{ ...th, textAlign: 'right' }}>Packs</th>
                    <th style={th}>Crab meat used</th>
                    <th style={{ ...th, textAlign: 'right' }}>Cost a pack</th>
                    <th style={th}>Confirmed</th>
                    <th style={th} />
                  </tr>
                </thead>
                <tbody>
                  {doneRuns.map((run) => {
                    const size = SIZES.find((s) => s.key === run.sizeKey) || SIZES[0];
                    const c = costs.byRun[run.id];
                    const meat = CRAB_SKUS.filter((x) => n((run.lbs || {})[x.sku]) > 0);
                    return (
                      <tr key={run.id}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{longDate(run.date)}</td>
                        <td style={{ ...td, fontWeight: 600, color: 'var(--navy)' }}>{size.label}</td>
                        <td style={{ ...td, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
                          {run.confirmed.batch || '—'}
                          {run.confirmed.flagged ? <div style={{ ...sub, color: '#b45309' }}>yield {run.confirmed.pct}% of expected, flagged</div> : null}
                        </td>
                        <td style={{ ...td, ...num }}>{fmt(run.confirmed.packs, 0)}{n(run.confirmed.loose) ? <div style={sub}>{fmt(run.confirmed.loose, 0)} loose</div> : null}{(run.confirmed.outputs || []).length ? <div style={sub}>{run.confirmed.outputs.map((o) => `${o.qty} ${o.loc}`).join(', ')}</div> : null}</td>
                        <td style={td}>{meat.map((x) => <div key={x.sku}>{fmt(run.lbs[x.sku], 2)} lb {x.name}<span style={{ ...sub, marginLeft: 4 }}>{x.sku}</span></div>)}</td>
                        <td style={{ ...td, ...num }}>{c ? money(c.perPack) : '—'}<div style={sub}>{c && c.noLabor ? 'no labor yet' : 'with labor'}</div></td>
                        <td style={{ ...td, fontSize: 11, color: 'var(--gray-700)' }}>
                          {run.confirmed.by || ''}
                          <div style={sub}>{run.confirmed.at ? new Date(run.confirmed.at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</div>
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <button type="button" className="btn btn-outline" onClick={() => printRun(run)} title="Print the run sheet with its batch number"><Printer size={14} style={{ marginRight: 6 }} />Print sheet</button>
                          <button type="button" className="btn btn-ghost" onClick={() => removeRun(run.id)} title="Remove this run" aria-label="Remove this run"><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 8 }}>
            Print sheet reprints the run sheet with the units produced and the CPX Catch batch number filled in, to copy onto the tote labels.
          </div>
        </div>
      </Section>
    </div>
  );
}
